/**
 * [HF2-6 v1.1] FlipCard - shared two-faced card-flip primitive.
 *
 * Pattern lifted from NominationLottoModal.jsx LottoCard (reference impl).
 * Replaces the auction SoldCell's fade-rotate animation in StandaloneDraftBoard
 * (which read as "fill in" rather than "card turning over" because only the
 * inner content rotated while the container border stayed fixed).
 *
 * Snake StandaloneBoard (Phase 2c.6-A §2.3) will use the same primitive
 * automatically when those tiles ship.
 *
 * Props:
 *   - front     : ReactNode rendered on the FRONT face (visible when flipped)
 *   - back      : ReactNode rendered on the BACK face (visible when not flipped)
 *   - isFlipped : boolean - when true, the card rotates 0→180° to show `front`
 *   - duration  : flip duration in seconds (default 1.0, lotto uses 1.0, range 0.8-1.2)
 *   - perspective : px value applied to the parent for the 3D effect (default 1000)
 *   - className / style : forwarded to outer container
 *
 * Critical CSS contract:
 *   • Parent has perspective set so the rotation reads as 3D.
 *   • Inner motion.div has transformStyle: preserve-3d so both faces rotate together.
 *   • Each face has backfaceVisibility: hidden so the back disappears mid-flip,
 *     letting the parent background show through (~90° mark = pure background).
 */
import React from "react";
import { motion } from "framer-motion";

export default function FlipCard({
  front,
  back,
  isFlipped,
  duration = 1.0,
  perspective = 1000,
  className = "",
  style = {},
  testId,
}) {
  return (
    <div
      className={className}
      style={{ perspective: `${perspective}px`, width: "100%", height: "100%", ...style }}
      data-testid={testId}
    >
      <motion.div
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration, ease: [0.4, 0, 0.2, 1] }}
        style={{
          transformStyle: "preserve-3d",
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {/* Back face - visible when isFlipped === false */}
        <div
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        >
          {back}
        </div>
        {/* Front face - visible when isFlipped === true (pre-rotated 180°) */}
        <div
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        >
          {front}
        </div>
      </motion.div>
    </div>
  );
}
